import Encoding from 'encoding-japanese'
import dayjs from 'dayjs'
import { getSagawaTimeCode } from './deliveryTimeZones'

// 【e飛伝Ⅲ】送り状データCSV出力項目レイアウト（ヘッダ有・74列）
export const SAGAWA_CSV_HEADER = [
  'お届け先コード取得区分',
  'お届け先コード',
  'お届け先電話番号',
  'お届け先郵便番号',
  'お届け先住所１',
  'お届け先住所２',
  'お届け先住所３',
  'お届け先名称１',
  'お届け先名称２',
  'お客様管理番号',
  'お客様コード',
  '部署・担当者コード取得区分',
  '部署・担当者コード',
  '部署・担当者名称',
  '荷送人電話番号',
  'ご依頼主コード取得区分',
  'ご依頼主コード',
  'ご依頼主電話番号',
  'ご依頼主郵便番号',
  'ご依頼主住所１',
  'ご依頼主住所２',
  'ご依頼主名称１',
  'ご依頼主名称２',
  '荷扱い',
  '品名１',
  '品名２',
  '品名３',
  '品名４',
  '品名５',
  '荷札数量',
  '荷札数量1',
  '荷札数量2',
  '荷札数量3',
  '荷札数量4',
  '荷札数量5',
  '荷札数量6',
  '荷札数量7',
  '荷札数量8',
  '荷札数量9',
  '荷札数量10',
  '荷札数量11',
  '出荷個数',
  'スピード指定',
  'クール便指定',
  '配達日',
  '配達指定時間帯',
  '配達指定時間(時分)',
  '代引金額',
  '消費税',
  '決済種別',
  '保険金額',
  '指定シール1',
  '指定シール2',
  '指定シール3',
  '営業所受取区分',
  'SRC区分',
  '営業所受取営業店コード',
  '元着区分',
  'メールアドレス',
  'ご不在時連絡先',
  '出荷日',
  'お問い合わせ送り状No',
  '出荷指定店区分',
  '集約解除指定',
  '編集01',
  '編集02',
  '編集03',
  '編集04',
  '編集05',
  '編集06',
  '編集07',
  '編集08',
  '編集09',
  '編集10',
]

// 出荷元（ご依頼主）情報
const ISSUER = {
  name: 'りんどう',
  address: '北海道歌志内市字歌神36',
  phone: '0125-35-9107',
}

// クール便指定コード
const COOL_REFRIGERATED = '002'
const COOL_FROZEN = '003'

// 文字列を chunkSize 文字ごとに count 個に分割する（e飛伝の住所・名称欄分割用）
const splitChunks = (text: string, chunkSize: number, count: number): string[] => {
  const chunks: string[] = []
  for (let i = 0; i < count; i++) {
    chunks.push(text.slice(i * chunkSize, (i + 1) * chunkSize))
  }
  return chunks
}

interface DeliveryRecipientInfo {
  name?: string
  postalCode?: string
  prefecture?: string
  address?: string
  phone?: string
}

interface SagawaCsvProduct {
  storageType?: string
}

export interface SagawaCsvShipment {
  id: string
  customer?: string
  shippingDate?: string
  deliveryTimeZone?: string
  products?: SagawaCsvProduct[]
  deliveryRecipient?: DeliveryRecipientInfo
  itemName2?: string
  itemName3?: string
  itemName4?: string
}

export interface SagawaCsvCustomer {
  companyAddress?: string
  companyTel?: string
}

export const buildSagawaCsvRow = (
  shipment: SagawaCsvShipment, customer?: SagawaCsvCustomer
): string[] => {
  const row = new Array(SAGAWA_CSV_HEADER.length).fill('')
  const recipient = shipment.deliveryRecipient

  row[2] = recipient?.phone || customer?.companyTel || '' // お届け先電話番号
  row[3] = recipient?.postalCode || '' // お届け先郵便番号

  const destAddress = recipient
    ? `${recipient.prefecture || ''}${recipient.address || ''}`
    : (customer?.companyAddress || '')
  const [addr1, addr2, addr3] = splitChunks(destAddress, 16, 3)
  row[4] = addr1 // お届け先住所１
  row[5] = addr2 // お届け先住所２
  row[6] = addr3 // お届け先住所３

  const destName = recipient?.name || shipment.customer || ''
  const [name1, name2] = splitChunks(destName, 16, 2)
  row[7] = name1 // お届け先名称１
  row[8] = name2 // お届け先名称２

  // 半角英数16文字制限のため、FirestoreドキュメントIDの先頭16文字を識別用に使用
  row[9] = (shipment.id || '').slice(0, 16) // お客様管理番号

  row[17] = ISSUER.phone // ご依頼主電話番号
  const [issuerAddr1, issuerAddr2] = splitChunks(ISSUER.address, 16, 2)
  row[19] = issuerAddr1 // ご依頼主住所１
  row[20] = issuerAddr2 // ご依頼主住所２
  row[21] = ISSUER.name // ご依頼主名称１

  row[24] = '鹿肉' // 品名１（固定）
  row[25] = shipment.itemName2 || '' // 品名２
  row[26] = shipment.itemName3 || '' // 品名３
  row[27] = shipment.itemName4 || '' // 品名４

  row[41] = '1' // 出荷個数（固定）

  // クール便指定：チルド商品が1つでもあれば冷蔵便、なければ冷凍便
  const products = Array.isArray(shipment.products) ? shipment.products : []
  row[43] = products.some((p) => p.storageType === 'チルド')
    ? COOL_REFRIGERATED
    : COOL_FROZEN

  row[45] = getSagawaTimeCode(shipment.deliveryTimeZone || '指定なし') // 配達指定時間帯

  row[60] = shipment.shippingDate // 出荷日
    ? dayjs(shipment.shippingDate.replace(/\//g, '-')).format('YYYYMMDD')
    : ''

  return row
}

const escapeCsvValue = (value: string) => `"${String(value ?? '').replace(/"/g, '""')}"`

// e飛伝Ⅲ取込用にShift-JISエンコードしたCSVのBlobを生成する
export const generateSagawaCsv = (rows: string[][]): Blob => {
  const text = [SAGAWA_CSV_HEADER, ...rows]
    .map((row) => row.map(escapeCsvValue).join(','))
    .join('\r\n')
  const unicodeArray = Encoding.stringToCode(text)
  const sjisArray = Encoding.convert(unicodeArray, { to: 'SJIS', from: 'UNICODE' })
  return new Blob([new Uint8Array(sjisArray)], { type: 'text/csv' })
}

// 出荷履歴CSVの列インデックス（お問合せ送り状№,出荷日時,...,お客様管理ナンバー,...）
const HISTORY_TRACKING_NUMBER_COL = 0 // お問合せ送り状№
const HISTORY_CUSTOMER_MANAGEMENT_NUMBER_COL = 12 // お客様管理ナンバー

export interface SagawaShippingRecord {
  trackingNumber: string
  customerManagementNumber: string
}

// CSVの1行をカンマ区切りで分割する（ダブルクォート囲み・エスケープに対応）
const parseCsvLine = (line: string): string[] => {
  const result: string[] = []
  let current = ''
  let inQuotes = false
  for (let i = 0; i < line.length; i++) {
    const char = line[i]
    if (inQuotes) {
      if (char === '"') {
        if (line[i + 1] === '"') {
          current += '"'
          i++
        } else {
          inQuotes = false
        }
      } else {
        current += char
      }
    } else if (char === '"') {
      inQuotes = true
    } else if (char === ',') {
      result.push(current)
      current = ''
    } else {
      current += char
    }
  }
  result.push(current)
  return result
}

// Shift-JISエンコードされた佐川急便の出荷履歴CSVファイルをUTF-8文字列に変換する
export const decodeSagawaCsvFile = async (file: File): Promise<string> => {
  const buffer = await file.arrayBuffer()
  const unicodeArray = Encoding.convert(new Uint8Array(buffer), 'UNICODE', 'SJIS')
  return Encoding.codeToString(unicodeArray)
}

// 佐川急便の出荷履歴CSV（ヘッダ行あり）から お問合せ送り状№ と お客様管理ナンバー を取得する
export const parseSagawaShippingHistoryCsv = (text: string): SagawaShippingRecord[] => {
  const lines = text.split(/\r\n|\r|\n/).filter((line) => line.trim() !== '')
  const records: SagawaShippingRecord[] = []
  // 先頭行はヘッダのためスキップ
  for (let i = 1; i < lines.length; i++) {
    const cols = parseCsvLine(lines[i])
    const trackingNumber = (cols[HISTORY_TRACKING_NUMBER_COL] || '').trim()
    const customerManagementNumber = (cols[HISTORY_CUSTOMER_MANAGEMENT_NUMBER_COL] || '').trim()
    if (!trackingNumber || !customerManagementNumber) continue
    records.push({ trackingNumber, customerManagementNumber })
  }
  return records
}
